import AppStateService from '@ajs/services/AppStateService';
import { Injectable, OnDestroy, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SURFACE_IMPORT_CREDENTIALS_STORE_URL, SURFACE_STORE_APP_ID } from '@app/core/models/constants/surface.constants';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { ErrorService } from '@app/core/services/error.service';
import { ExportInterface } from '@app/exports/models/interfaces/export.interface';
import { VaultRowModel } from '@app/vault/models/vault-row.model';
import { VaultDataService } from '@app/vault/services/vault-data.service';
import { VaultParsingBigCommerceService } from '@app/vault/services/vault-parsing-bigcommerce.service';
import { BehaviorSubject, catchError, combineLatest, filter, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { LogService } from './log.service';

/**
 * This is a stateful service focused on creating / accessing the store link to the BigCommerce [BC] application.
 * We store BC unique identifiers within the vault - we must retrieve this info in order to link back to BC.
 * Each database has its own vault, so we must listen for database changes.
 */
@Injectable({
    providedIn: 'root'
})
export class BigCommerceLinkService implements OnDestroy {
    protected readonly unsubscribe$: Subject<void> = new Subject<void>();

    /**
     * This emits when the db has changed, the account has a BC SS feature flag, and there BigCommerce keys in the vault
     * @private
     */
    private readonly _bcVaultKeys$: BehaviorSubject<Record<string, string | number> | null> = new BehaviorSubject<Record<string, string | number> | null>(null);

    baseUrl$: Observable<string> = this._bcVaultKeys$.pipe(
        filter((vaultRecord) => vaultRecord !== null && Boolean(vaultRecord?.[SURFACE_IMPORT_CREDENTIALS_STORE_URL])),
        map((vaultRecord) => {
            const storeUrl: string | number = vaultRecord?.[SURFACE_IMPORT_CREDENTIALS_STORE_URL];
            return this.domSanitizer.sanitize(SecurityContext.URL, storeUrl);
        })
    );

    appUrl$: Observable<string> = this.baseUrl$.pipe(
        map((storeUrl) => {
            return `${storeUrl}/manage/app/${SURFACE_STORE_APP_ID}`;
        })
    );

    planUrl$: Observable<string> = this.appUrl$.pipe(
        map((storeUrl) => `${storeUrl}/plans`)
    );

    /**
     * The products link - NOT filtered by a specific channel id
     * See the `BigCommerceProductsLinkService` for channel specific product url.
     */
    productsUrl$: Observable<string> = this.baseUrl$.pipe(
        map((storeUrl) => this.getProductUrl(storeUrl))
    );

    getProductUrl(storeUrl: string): string {
        return `${storeUrl}/manage/products?direction=asc&sort=id`;
    }

    /**
     * Builds the BigCommerce agentic onboarding URL for the storefront channel.
     */
    getAgenticOnboardingUrl$(channelName: string, storefrontId: number): Observable<string | null> {
        if (!this.bigCommerceMode.accountHasBcFeatureFlag || !channelName || !storefrontId) {
            return of(null);
        }

        return this.baseUrl$.pipe(
            map((storeUrl) => {
                const rawUrl = `${storeUrl}/manage/channels/agentic/${channelName}/${storefrontId}`;
                return this.domSanitizer.sanitize(SecurityContext.URL, rawUrl);
            })
        );
    }

    constructor(
        private readonly appStateService: AppStateService,
        private readonly bigCommerceMode: BigCommerceModeStateService,
        private readonly domSanitizer: DomSanitizer,
        private readonly errorService: ErrorService,
        private readonly logService: LogService,
        private readonly vaultService: VaultDataService,
        private readonly vaultParsingBigCommerceService: VaultParsingBigCommerceService
    ) {
        this.bindVaultDataToDbChange();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this._bcVaultKeys$.complete();
    }

    private bindVaultDataToDbChange(): void {
        this.appStateService.databaseLoaded$.pipe(
            tap(() => this._bcVaultKeys$.next(null)),
            filter((database) => database?.id && this.bigCommerceMode.accountHasBcFeatureFlag),
            switchMap((database) => {
                return this.vaultService.getBcImportCredentialsVault(database.id).pipe(
                    catchError((error) => {
                        this.logService.error(this.errorService.getErrorMessage(error));
                        return of(null);
                    })
                );
            }),
            filter((vaultData: VaultRowModel | null) => vaultData !== null),
            tap((vaultRecord: VaultRowModel) => {
                const vaultKeys = vaultRecord.parseCredentials<Record<string, string | number>>();
                this._bcVaultKeys$.next(vaultKeys);
            }),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    /**
     * We use this export to grab the specific channel id from the vault.
     *
     * If the channel information does NOT exist, we return the regular products page.
     * If the channel information does exist, we return the products page filtered down by the specific channel id
     */
    public getProductsUrlForChannel(currentExport: Partial<ExportInterface>): Observable<string> {
        if (!this.bigCommerceMode.accountHasBcFeatureFlag) {
            return of('');
        }

        if (!currentExport) {
            return this.productsUrl$;
        }

        const channelVaultName = this.getChannelVaultName(currentExport);
        if (!channelVaultName) {
            return this.productsUrl$;
        }

        return combineLatest([
            this.productsUrl$,
            this.vaultService.getAll(this.appStateService.getDatabaseId(), {'vault_name': channelVaultName})
        ]).pipe(
            map(([productsUrl, exportVaultRecord]) => this.formatUrl(productsUrl, exportVaultRecord)),
            catchError((error) => {
                const errorMessage = this.errorService.getErrorMessage(error);
                this.logService.error(errorMessage);
                return this.productsUrl$;
            })
        );
    }

    private getChannelVaultName(currentExport: Partial<ExportInterface>): string | null  {
        if (!currentExport.destination) {
            return null;
        }

        const destination = currentExport.destination.toLowerCase();
        if (destination.includes('meta')) {
            return `metaMBE_${currentExport.id}`;
        }

        if (destination.includes('google')) {
            return `googleGMC_${currentExport.id}`;
        }

        // Todo: Future channels go here
        return null;
    }

    private formatUrl(productsUrl: string, exportVaultRecord: VaultRowModel[]): string | null {
        if (!productsUrl) {
            this.logService.error('Vault record is missing store url. Cannot generate BC products url.');
            return null;
        }

        const channelId: string | null = this.vaultParsingBigCommerceService.getChannelIdFromVaultRecord(exportVaultRecord);

        // If there is no channel id available, omit that query parameter from the URL
        const rawUrl = `${productsUrl}${channelId ? `&channelIdsIn=${channelId}` : ''}`;
        return this.domSanitizer.sanitize(SecurityContext.URL, rawUrl);
    }
}
